import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, BellIcon, CheckCircle2Icon, Loader2Icon, RotateCcwIcon } from 'lucide-react';
import { chapters, media } from '../data/chapters';
import type { Answers } from '../types/pulse';
interface SummaryScreenProps {
  answers: Answers;
  peerGroup: string;
  onRestart: () => void;
  onOpenResults: () => void;
}
type NotifyState = 'idle' | 'saving' | 'done';
const ease = [0.22, 1, 0.36, 1] as const;
export function SummaryScreen({
  answers,
  peerGroup,
  onRestart,
  onOpenResults
}: SummaryScreenProps) {
  const [notify, setNotify] = useState<NotifyState>('idle');
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  const handleNotify = () => {
    setNotify('saving');
    timer.current = window.setTimeout(() => setNotify('done'), 800);
  };
  return <div className="h-full overflow-y-auto bg-mist-50 no-scrollbar" data-id="element-742" data-mp="39xkt1ot7sg7o">
      <div className="relative h-[220px] overflow-hidden bg-mist-100" data-id="element-743" data-mp="vfhqa5x640qa">
        <motion.img src={media.finale} alt="" initial={{
        scale: 1.2
      }} animate={{
        scale: 1.02
      }} transition={{
        duration: 10,
        ease: 'easeOut'
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-744" data-mp="zguygmnqgg0d" />
      </div>

      <div className="relative -mt-8 rounded-t-[28px] bg-mist-50 px-5 pb-10 pt-6" data-id="element-745" data-mp="br8vdo48pgn3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-pulse-teal" data-id="element-746" data-mp="ghpadcnplabk">Your IVF Pulse</p>
        <motion.h1 initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease
      }} className="mt-2 font-serif text-[32px] font-normal leading-[1.05] text-navy-900" data-id="element-747" data-mp="2qkkv3e21yey">
          Your perspective.
          <br data-id="element-748" data-mp="3twlwbd1xmu14" />
          <span className="italic text-pulse-teal" data-id="element-749" data-mp="3tx5440xvc21f">The bigger picture.</span>
        </motion.h1>
        <p className="mt-3 text-[13px] text-slate-500" data-id="element-750" data-mp="3asih03faah6w">
          Compared with <span className="font-medium text-navy-900" data-id="element-751" data-mp="3attly6kgtuzl">{peerGroup}</span>
        </p>

        <ol className="mt-6 space-y-3" data-id="element-752" data-mp="35zz12zt4j9yx">
          {chapters.map((c, i) => {
          const a = answers[c.id];
          const main = c.options.find(o => o.id === a?.main);
          const reason = a?.main ? c.followUps[a.main]?.options.find(o => o.id === a.followUp) : undefined;
          return <motion.li key={c.id} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.4 + i * 0.12,
            ease
          }} className="relative overflow-hidden rounded-3xl border border-navy-900/[0.08] bg-white p-5" data-id="element-753" data-mp="3toibplznbyu6" data-mpi={i}>
                <span className="absolute inset-y-0 left-0 w-1" style={{
              backgroundColor: c.accent
            }} data-id="element-754" data-mp="28ekw86xd36v7" />
                <div className="flex items-center justify-between" data-id="element-755" data-mp="23dnhk4bk8uyg">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{
                color: c.accent
              }} data-id="element-756" data-mp="iwwh08z71s1p">
                    {c.number} · {c.title}
                  </p>
                  {main && <p className="text-[11px] tabular-nums text-slate-500" data-id="element-757" data-mp="suaj13kdeeah">
                      {main.peerPercent}% aligned<span className="text-slate-400" data-id="element-758" data-mp="xlkbsd17txql">*</span>
                    </p>}
                </div>
                <p className="mt-2 font-serif text-[20px] leading-snug text-navy-900" data-id="element-759" data-mp="12ju1w983285c">
                  {main ? main.short : 'Not answered'}
                </p>
                {reason && <p className="mt-1 text-[13px] leading-snug text-slate-500" data-id="element-760" data-mp="1bll8rhed0l0s">
                    Because: {reason.label.charAt(0).toLowerCase() + reason.label.slice(1)}
                  </p>}
              </motion.li>;
        })}
        </ol>
        <p className="mt-3 text-[10px] text-slate-400" data-id="element-761" data-mp="3iaq40w1bq6yx">* Illustrative demonstration figures, not survey findings.</p>

        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.9,
        ease
      }} className="mt-8 rounded-3xl bg-navy-900 p-6 text-white" aria-labelledby="invite-heading" data-id="element-762" data-mp="1clxt8yol50gs">
          <h2 id="invite-heading" className="font-serif text-[24px] font-light leading-tight" data-id="element-763" data-mp="1hm28ymho5r5y">
            Where does the fertility community agree — and where does it disagree?
          </h2>
          <p className="mt-3 text-[14px] leading-relaxed text-white/70" data-id="element-764" data-mp="oyfxyt643k92">
            The collective findings from participating fertility specialists will be explored at ESHRE 2027.
          </p>

          <AnimatePresence mode="wait" initial={false} data-id="element-765" data-mp="xy305h6o0u36">
            {notify === 'done' ? <motion.div key="done" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mt-5 rounded-2xl bg-white/10 p-4" role="status" data-id="element-766" data-mp="a88mixeiv1hv">
                <p className="flex items-center gap-2 text-[14px] font-medium" data-id="element-767" data-mp="3s6jdwzqy266o">
                  <CheckCircle2Icon className="h-4 w-4 text-teal-300" data-id="element-768" data-mp="vhxwc1xr8s3" />
                  Interest noted for this demo
                </p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-white/65" data-id="element-769" data-mp="3n9j8mr1x57gm">
                  No personal information has been collected. In a live version, registration, consent and data handling
                  would be configured and approved before use.
                </p>
              </motion.div> : <motion.button key="cta" type="button" onClick={handleNotify} disabled={notify === 'saving'} className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-white text-[15px] font-semibold text-navy-900 transition hover:bg-mist-100 disabled:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-300" data-id="element-770" data-mp="4wbnrayxuxnm">
                {notify === 'saving' ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-771" data-mp="w2invl30m6o8" /> : <BellIcon className="h-4 w-4 text-pulse-teal" data-id="element-772" data-mp="rc28vwjjqczr" />}
                {notify === 'saving' ? 'Saving…' : 'Notify me when results are available'}
              </motion.button>}
          </AnimatePresence>
          <p className="mt-3 text-center text-[11px] text-white/50" data-id="element-773" data-mp="hfpf6yo03z50">Optional · Illustrative invitation only</p>
        </motion.section>

        <button type="button" onClick={onOpenResults} className="mt-4 flex w-full items-center justify-between rounded-2xl border border-navy-900/10 bg-white px-5 py-4 text-left transition hover:border-navy-900/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-teal" data-id="element-774" data-mp="dex2fvfx7c3z">
          <span data-id="element-775" data-mp="1xrw64norgsh3">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-pulse-iris" data-id="element-776" data-mp="1xtfmsfebx5je">After the congress</span>
            <span className="mt-1 block text-[15px] font-medium text-navy-900" data-id="element-777" data-mp="25bn915sjorad">Preview the Results Snak</span>
          </span>
          <ArrowRightIcon className="h-4 w-4 text-navy-700" data-id="element-778" data-mp="2f1q91py335i7" />
        </button>

        <button type="button" onClick={onRestart} className="mx-auto mt-6 flex items-center gap-2 rounded-full px-4 py-2 text-[13px] text-slate-500 transition hover:text-navy-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-900/30" data-id="element-779" data-mp="2f3t972syaf0n">
          <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-780" data-mp="1rce26wq8b9j2" />
          Explore a different path
        </button>

        <p className="mt-6 border-t border-navy-900/5 pt-5 text-[10px] leading-relaxed text-slate-400" data-id="element-781" data-mp="1wdbjl73ezzji">
          Concept prototype for a non-promotional peer-perspective experience. Sponsor disclosure will be included in the
          production version. All medical content is subject to review.
        </p>
      </div>
    </div>;
}